import { c as _c } from "react/compiler-runtime";
import { IcoPlusSmall16, IcoTrash16 } from '@onefootprint/icons';
import { useMemo } from 'react';
import IconButton from '../../../../../icon-button';
import LinkButton from '../../../../../link-button';
import TextInput from '../../../../../text-input';
import { type CompositeMode, findDuplicateNames, generateId, type SchemaProperty, type SchemaPropertyType } from '../../../../utils/schema-model';
import { INDENT_PER_DEPTH } from '../../constants';
import EnumEditor from '../enum-editor';
import PropertyRow from '../property-row';
import TypeSelector from '../type-selector';
type CompositeSchemaRowProps = {
  schema: SchemaProperty;
  onChange: (schema: SchemaProperty) => void;
  onDelete: () => void;
  depth: number;
  isDisabled?: boolean;
};

/**
 * CompositeSchemaRow renders a schema within a composite type (anyOf/oneOf/allOf)
 * Unlike PropertyRow, schemas don't have names - they're just type definitions
 */
const CompositeSchemaRow = t0 => {
  const $ = _c(82);
  const {
    schema,
    onChange,
    onDelete,
    depth,
    isDisabled: t1
  } = t0;
  const isDisabled = t1 === undefined ? false : t1;
  const indent = depth * INDENT_PER_DEPTH;
  const childIndent = (depth + 1) * INDENT_PER_DEPTH;
  let t2;
  if ($[0] !== onChange || $[1] !== schema) {
    t2 = (type, arrayItemType, compositeMode) => {
      const updated = {
        ...schema,
        type
      };
      if (type === "array") {
        updated.arrayItemType = arrayItemType || "string";
        if (arrayItemType === "object") {
          updated.arrayItemChildren = updated.arrayItemChildren || [];
        }
      } else {
        if (type === "object") {
          updated.children = updated.children || [];
        } else {
          if (type === "enum") {
            updated.enumValues = updated.enumValues || [];
          } else {
            if (type === "composite") {
              updated.compositeMode = compositeMode || "oneOf";
              updated.schemas = updated.schemas || [];
            }
          }
        }
      }
      onChange(updated);
    };
    $[0] = onChange;
    $[1] = schema;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleTypeChange = t2;
  let t3;
  if ($[3] !== onChange || $[4] !== schema) {
    t3 = description => {
      onChange({
        ...schema,
        description: description || undefined
      });
    };
    $[3] = onChange;
    $[4] = schema;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleDescriptionChange = t3;
  let t4;
  if ($[6] !== onChange || $[7] !== schema) {
    t4 = values => {
      onChange({
        ...schema,
        enumValues: values
      });
    };
    $[6] = onChange;
    $[7] = schema;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleEnumValuesChange = t4;
  let t5;
  if ($[9] !== onChange || $[10] !== schema) {
    t5 = (childIndex, child) => {
      if (schema.type === "object") {
        const newChildren = [...(schema.children || [])];
        newChildren[childIndex] = child;
        onChange({
          ...schema,
          children: newChildren
        });
      } else {
        if (schema.type === "array" && schema.arrayItemType === "object") {
          const newChildren_0 = [...(schema.arrayItemChildren || [])];
          newChildren_0[childIndex] = child;
          onChange({
            ...schema,
            arrayItemChildren: newChildren_0
          });
        }
      }
    };
    $[9] = onChange;
    $[10] = schema;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleChildChange = t5;
  let t6;
  if ($[12] !== onChange || $[13] !== schema) {
    t6 = childIndex_0 => {
      if (schema.type === "object") {
        const newChildren_1 = (schema.children || []).filter((_, i) => i !== childIndex_0);
        onChange({
          ...schema,
          children: newChildren_1
        });
      } else {
        if (schema.type === "array" && schema.arrayItemType === "object") {
          const newChildren_2 = (schema.arrayItemChildren || []).filter((__0, i_0) => i_0 !== childIndex_0);
          onChange({
            ...schema,
            arrayItemChildren: newChildren_2
          });
        }
      }
    };
    $[12] = onChange;
    $[13] = schema;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const handleChildDelete = t6;
  let t7;
  if ($[15] !== onChange || $[16] !== schema) {
    t7 = () => {
      const existingChildren = schema.children || schema.arrayItemChildren || [];
      const existingNames = new Set(existingChildren.map(_temp));
      let counter = existingChildren.length + 1;
      let newName = `field_${counter}`;
      while (existingNames.has(newName)) {
        counter++;
        newName = `field_${counter}`;
      }
      const newChild = {
        id: generateId(),
        name: newName,
        type: "string"
      };
      if (schema.type === "object") {
        onChange({
          ...schema,
          children: [...(schema.children || []), newChild]
        });
      } else {
        if (schema.type === "array" && schema.arrayItemType === "object") {
          onChange({
            ...schema,
            arrayItemChildren: [...(schema.arrayItemChildren || []), newChild]
          });
        }
      }
    };
    $[15] = onChange;
    $[16] = schema;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const handleAddChild = t7;
  let t8;
  if ($[18] !== onChange || $[19] !== schema) {
    t8 = (schemaIndex, nestedSchema) => {
      const newSchemas = [...(schema.schemas || [])];
      newSchemas[schemaIndex] = nestedSchema;
      onChange({
        ...schema,
        schemas: newSchemas
      });
    };
    $[18] = onChange;
    $[19] = schema;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const handleNestedSchemaChange = t8;
  let t9;
  if ($[21] !== onChange || $[22] !== schema) {
    t9 = schemaIndex_0 => {
      const newSchemas_0 = (schema.schemas || []).filter((__1, i_1) => i_1 !== schemaIndex_0);
      onChange({
        ...schema,
        schemas: newSchemas_0
      });
    };
    $[21] = onChange;
    $[22] = schema;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const handleNestedSchemaDelete = t9;
  let t10;
  if ($[24] !== onChange || $[25] !== schema) {
    t10 = () => {
      const existingSchemas = schema.schemas || [];
      const newSchema = {
        id: generateId(),
        name: `schema_${existingSchemas.length + 1}`,
        type: "string"
      };
      onChange({
        ...schema,
        schemas: [...existingSchemas, newSchema]
      });
    };
    $[24] = onChange;
    $[25] = schema;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const handleAddNestedSchema = t10;
  const children = schema.type === "object" ? schema.children : schema.arrayItemChildren;
  const isObjectType = schema.type === "object";
  const isArrayOfObjects = schema.type === "array" && schema.arrayItemType === "object";
  const isComposite = schema.type === "composite";
  const canHaveChildren = isObjectType || isArrayOfObjects;
  let t11;
  bb0: {
    if (!children) {
      let t12;
      if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
        t12 = new Set();
        $[27] = t12;
      } else {
        t12 = $[27];
      }
      t11 = t12;
      break bb0;
    }
    let t12;
    if ($[28] !== children) {
      t12 = findDuplicateNames(children);
      $[28] = children;
      $[29] = t12;
    } else {
      t12 = $[29];
    }
    t11 = t12;
  }
  const childDuplicates = t11;
  let t12;
  if ($[30] !== indent) {
    t12 = {
      paddingLeft: indent
    };
    $[30] = indent;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  const t13 = schema.description || "";
  let t14;
  if ($[32] !== handleDescriptionChange || $[33] !== isDisabled || $[34] !== t13) {
    t14 = <TextInput size="compact" value={t13} onChangeText={handleDescriptionChange} placeholder="Description (optional)" disabled={isDisabled} containerClassName="min-w-0 flex-1 [&_.fp-input-container]:mb-0" />;
    $[32] = handleDescriptionChange;
    $[33] = isDisabled;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== handleTypeChange || $[37] !== isDisabled || $[38] !== schema.arrayItemType || $[39] !== schema.compositeMode || $[40] !== schema.type) {
    t15 = <TypeSelector type={schema.type} arrayItemType={schema.arrayItemType} compositeMode={schema.compositeMode} onChange={handleTypeChange} isDisabled={isDisabled} />;
    $[36] = handleTypeChange;
    $[37] = isDisabled;
    $[38] = schema.arrayItemType;
    $[39] = schema.compositeMode;
    $[40] = schema.type;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== isDisabled || $[43] !== onDelete) {
    t16 = <IconButton aria-label="Delete schema" onClick={onDelete} size="compact" icon={IcoTrash16} destructive={true} disabled={isDisabled} />;
    $[42] = isDisabled;
    $[43] = onDelete;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== t15 || $[46] !== t16) {
    t17 = <div className="flex flex-shrink-0 items-stretch gap-1 self-stretch">{t15}{t16}</div>;
    $[45] = t15;
    $[46] = t16;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== t12 || $[49] !== t14 || $[50] !== t17) {
    t18 = <div className="flex items-center gap-3" style={t12}>{t14}{t17}</div>;
    $[48] = t12;
    $[49] = t14;
    $[50] = t17;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] !== handleEnumValuesChange || $[53] !== indent || $[54] !== isDisabled || $[55] !== schema.enumValues || $[56] !== schema.type) {
    t19 = schema.type === "enum" && <div style={{
      paddingLeft: indent
    }}><EnumEditor values={schema.enumValues || []} onChange={handleEnumValuesChange} isDisabled={isDisabled} /></div>;
    $[52] = handleEnumValuesChange;
    $[53] = indent;
    $[54] = isDisabled;
    $[55] = schema.enumValues;
    $[56] = schema.type;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  let t20;
  if ($[58] !== childIndent || $[59] !== depth || $[60] !== handleAddNestedSchema || $[61] !== handleNestedSchemaChange || $[62] !== handleNestedSchemaDelete || $[63] !== isComposite || $[64] !== isDisabled || $[65] !== schema.schemas) {
    t20 = isComposite && <div className="flex flex-col gap-2">{(schema.schemas || []).map((nestedSchema_0, nestedIndex) => <CompositeSchemaRow key={nestedSchema_0.id} schema={nestedSchema_0} onChange={prop => handleNestedSchemaChange(nestedIndex, prop)} onDelete={() => handleNestedSchemaDelete(nestedIndex)} depth={depth + 1} isDisabled={isDisabled} />)}<div style={{
        paddingLeft: childIndent
      }}><LinkButton onClick={handleAddNestedSchema} iconComponent={IcoPlusSmall16} iconPosition="left" disabled={isDisabled}>Add schema</LinkButton></div></div>;
    $[58] = childIndent;
    $[59] = depth;
    $[60] = handleAddNestedSchema;
    $[61] = handleNestedSchemaChange;
    $[62] = handleNestedSchemaDelete;
    $[63] = isComposite;
    $[64] = isDisabled;
    $[65] = schema.schemas;
    $[66] = t20;
  } else {
    t20 = $[66];
  }
  let t21;
  if ($[67] !== canHaveChildren || $[68] !== childDuplicates || $[69] !== childIndent || $[70] !== children || $[71] !== depth || $[72] !== handleAddChild || $[73] !== handleChildChange || $[74] !== handleChildDelete || $[75] !== isDisabled) {
    t21 = canHaveChildren && <div className="flex flex-col gap-2">{(children || []).map((child_0, childIndex_1) => <PropertyRow key={child_0.id} property={child_0} onChange={prop_0 => handleChildChange(childIndex_1, prop_0)} onDelete={() => handleChildDelete(childIndex_1)} depth={depth + 1} error={childDuplicates.has(child_0.name) ? "Duplicate property name" : undefined} isDisabled={isDisabled} />)}<div style={{
        paddingLeft: childIndent
      }}><LinkButton onClick={handleAddChild} iconComponent={IcoPlusSmall16} iconPosition="left" disabled={isDisabled}>Add property</LinkButton></div></div>;
    $[67] = canHaveChildren;
    $[68] = childDuplicates;
    $[69] = childIndent;
    $[70] = children;
    $[71] = depth;
    $[72] = handleAddChild;
    $[73] = handleChildChange;
    $[74] = handleChildDelete;
    $[75] = isDisabled;
    $[76] = t21;
  } else {
    t21 = $[76];
  }
  let t22;
  if ($[77] !== t18 || $[78] !== t19 || $[79] !== t20 || $[80] !== t21) {
    t22 = <div className="flex flex-col gap-2">{t18}{t19}{t20}{t21}</div>;
    $[77] = t18;
    $[78] = t19;
    $[79] = t20;
    $[80] = t21;
    $[81] = t22;
  } else {
    t22 = $[81];
  }
  return t22;
};
export default CompositeSchemaRow;
function _temp(c) {
  return c.name;
}